import { LinkProps } from "@tanstack/react-router"

import { FileRouteTypes } from "@/routeTree.gen"

type RoutePath = FileRouteTypes["to"]

/**
 * Paths whose `/qr/$qrLookupID` mirror is not simply the path appended to the
 * QR prefix. The QR tree's home is the welcome screen; the top-level tree's is
 * `/`.
 */
const MIRROR_OVERRIDES = {
  "/": "/qr/$qrLookupID/welcome",
} as const satisfies Record<string, RoutePath>

/**
 * Every top-level path that has a mirror inside the `/qr/$qrLookupID` tree.
 * Derived from the generated route tree, so mirroring a route on both sides is
 * all it takes to make it available here.
 */
export type ScopedPath =
  | keyof typeof MIRROR_OVERRIDES
  | {
      [P in RoutePath]: `/qr/$qrLookupID${P}` extends RoutePath ? P : never
    }[RoutePath]

/**
 * The path params a route declares, straight from the generated tree. Read off
 * the top-level path rather than its mirror, so it never includes
 * `qrLookupID` — that one is supplied here, not by the caller.
 */
type ParamsOf<T extends ScopedPath> =
  FileRouteTypes["fileRoutesByTo"][T]["types"]["allParams"]

/**
 * `params` as a trailing argument, required exactly when the target path
 * declares some. A path with no params takes no argument at all.
 */
type ScopedParamsArg<T extends ScopedPath> = keyof ParamsOf<T> extends never
  ? [params?: undefined]
  : [params: ParamsOf<T>]

/** The same requirement as a property, for callers that take an options object. */
export type ScopedParamsProp<T extends ScopedPath> =
  keyof ParamsOf<T> extends never
    ? { params?: undefined }
    : { params: ParamsOf<T> }

/*
 * TODO: cover this module with tests once the repo has a test runner (there is
 * none today — no vitest/jest, no test script). Most of what can go wrong here
 * is type-level and invisible at runtime, so the valuable cases are negative
 * ones, asserted with something like expect-type or @ts-expect-error:
 *
 *   - a path whose mirror is missing from one tree is rejected by ScopedPath
 *   - params is required when the path declares some, and absent when it
 *     doesn't: scopedTo("/receipt/$receiptID", id) and
 *     scopedTo("/account", id, { receiptID }) must both fail
 *   - a param name the path doesn't declare is rejected
 *   - useScopedNavigate checks search against the target route's schema, not
 *     the union of every route's
 *
 * Worth a runtime test too: scopedTo returns the top-level path when
 * qrLookupID is undefined, the mirror when it is set, and honors
 * MIRROR_OVERRIDES for "/".
 */

/** A resolved destination: exactly the fields that identify where to go. */
type ScopedDestination = Pick<LinkProps, "to" | "params">

/**
 * The `to`/`params` pair for a path that exists in both route trees: the
 * `/qr/$qrLookupID` mirror when a `qrLookupID` is in play, the top-level path
 * otherwise. Spread it into whatever takes a destination — `navigate`,
 * `redirect`, `buildLocation`, `matchRoute`, `<Link>`, `<LinkButton>` — so the
 * path is written once instead of branching per tree:
 *
 *     <Link {...scopedTo("/account", qrLookupID)}>Account</Link>
 *     <Link {...scopedTo("/receipt/$receiptID", qrLookupID, { receiptID })}>
 *
 * The two trees can't share one destination as far as the router's types are
 * concerned (`params` applies only to the parameterized tree), so the pair is
 * widened to `LinkProps` on the way out. `to` and `params` stay exactly typed
 * at the call site, which is where a wrong path or a missing param is worth
 * catching.
 */
export function scopedTo<T extends ScopedPath>(
  to: T,
  qrLookupID: string | undefined,
  ...[params]: ScopedParamsArg<T>
): ScopedDestination {
  /*
   * The generic is resolved by now, so the widening here costs the caller
   * nothing: it only lets one body serve both trees.
   */
  const pathParams = params as Record<string, string> | undefined

  if (!qrLookupID) return { to, params: pathParams } as ScopedDestination

  const qrTo =
    to in MIRROR_OVERRIDES
      ? MIRROR_OVERRIDES[to as keyof typeof MIRROR_OVERRIDES]
      : `/qr/$qrLookupID${to}`

  return {
    to: qrTo,
    params: { ...pathParams, qrLookupID },
  } as ScopedDestination
}
